import { QueryClient, queryOptions } from '@tanstack/react-query';
import type {
  EventDto,
  EventKind,
  GalleryCategoryDto,
  Locale,
  PageDto,
  PageSlug,
  ServiceDto,
  SiteSettingsDto,
  StatDto,
  VideoCategoryDto,
} from '@ceo/shared';
import { ApiError, api } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

export type EventsResponse = {
  kind: EventKind;
  years: number[];
  year: number | null;
  items: EventDto[];
};

/** Query option factories – shared by route loaders (prefetch) and components (read). */
export const q = {
  page: <S extends PageSlug>(slug: S, locale: Locale) =>
    queryOptions({
      queryKey: ['page', slug, locale],
      queryFn: () => api<PageDto<S>>(`/pages/${slug}`, { query: { locale } }),
    }),
  settings: (locale: Locale) =>
    queryOptions({
      queryKey: ['settings', locale],
      queryFn: () => api<SiteSettingsDto>('/settings', { query: { locale } }),
    }),
  gallery: (locale: Locale) =>
    queryOptions({
      queryKey: ['gallery', locale],
      queryFn: () => api<GalleryCategoryDto[]>('/gallery', { query: { locale } }),
    }),
  videos: (locale: Locale) =>
    queryOptions({
      queryKey: ['videos', locale],
      queryFn: () => api<VideoCategoryDto[]>('/videos', { query: { locale } }),
    }),
  stats: (locale: Locale) =>
    queryOptions({
      queryKey: ['stats', locale],
      queryFn: () => api<StatDto[]>('/stats', { query: { locale } }),
    }),
  services: (locale: Locale) =>
    queryOptions({
      queryKey: ['services', locale],
      queryFn: () => api<ServiceDto[]>('/services', { query: { locale } }),
    }),
  service: (slug: string, locale: Locale) =>
    queryOptions({
      queryKey: ['service', slug, locale],
      queryFn: () => api<ServiceDto>(`/services/${slug}`, { query: { locale } }),
    }),
  events: (params: { locale: Locale; kind?: EventKind; year?: number; featured?: boolean }) =>
    queryOptions({
      queryKey: ['events', params],
      queryFn: () => api<EventsResponse>('/events', { query: params }),
    }),
};
